import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useCompanyContext } from 'frontend/src/contexts';
import { useDashboardAnalytics } from 'frontend/src/contexts/DashboardAnalytics';
import React from 'react';
import { useHistory } from 'react-router';
import { formatNumber, pathname, providerName } from '../../../utils';
import Header from '../../misc/Header';
import Card from '../Card';
import AnalyticsTile from './AnalyticTile';
import CostOfGoodsChart from './CostOfGoodsChart';
import GoodsSoldGraph from './GoodsSoldGraph';
import RevenueBreakdownChart from './RevenueBreakdownChart';

const useStyles = makeStyles((theme: any) => {
  const smBreakpoint = theme.breakpoints.down('sm');
  return {
    cardContainer: {
      marginTop: theme.spacing(4),
    },
    graphGridMargin: {
      marginTop: theme.spacing(4.5),
      [smBreakpoint]: {
        marginTop: theme.spacing(2),
      },
    },
    rightSideTileWrapper: {
      paddingLeft: theme.spacing(1.5),
      [smBreakpoint]: {
        paddingLeft: 0,
      },
    },
    leftSideTileWrapper: {
      paddingRight: theme.spacing(1.5),
      marginBottom: 0,
      [smBreakpoint]: {
        paddingRight: 0,
        marginBottom: theme.spacing(2),
      },
    },
    gridMbBig: {
      marginBottom: 0,
      [smBreakpoint]: {
        marginBottom: theme.spacing(3),
      },
    },
  };
});

export default function DashboardAnalytics(props) {
  const classes = useStyles();
  const history = useHistory();
  const { business } = useCompanyContext();
  const { analytics, isLoading, errors } = useDashboardAnalytics();
  const { revenues = {}, leadTime = {}, leadTimeBenchmark } = analytics;
  const { revenueOnDate, projectedRevenue = 0 } = revenues;
  const { avgLeadTime = {} } = leadTime;
  const { amount, unit } = avgLeadTime;

  if (errors?.some(({ type }) => type === 'NO_LINESFOR_COMPANY')) {
    history.push(pathname.onboarding.main.createFirstLine.url);
    return null;
  }

  return (
    <Grid container>
      <Header header={`${business.name} Analytics`} />
      <Card title="" isGrey={true} className={classes.cardContainer}>
        <Grid direction="row" container>
          <Grid
            item
            md={6}
            xs={12}
            className={`${classes.leftSideTileWrapper} ${classes.gridMbBig}`}
          >
            <Grid item container justifyContent="space-between" direction="row">
              <Grid md={6} xs={12} item className={classes.leftSideTileWrapper}>
                <AnalyticsTile
                  title="Projected Revenue"
                  value={`$${formatNumber(projectedRevenue)}`}
                  isLoading={isLoading}
                  helpText={
                    'This is the revenue that is expected to be generated by your Lines.'
                  }
                />
              </Grid>
              <Grid
                md={6}
                xs={12}
                item
                className={classes.rightSideTileWrapper}
              >
                <AnalyticsTile
                  title="Revenue To Date"
                  value={`$${formatNumber(revenueOnDate) || 0}`}
                  isLoading={isLoading}
                  helpText={
                    'This is revenue that has been earned per the planned execution of your Lines. It is based on platform data, as available.'
                  }
                />
              </Grid>
            </Grid>
            <Grid item className={classes.graphGridMargin}>
              <RevenueBreakdownChart />
            </Grid>
          </Grid>
          <Grid item md={6} xs={12} className={classes.rightSideTileWrapper}>
            <Grid item container justifyContent="space-between" direction="row">
              <Grid md={6} xs={12} item className={classes.leftSideTileWrapper}>
                <AnalyticsTile
                  title="My Lead Time"
                  value={amount}
                  unit={unit}
                  isLoading={isLoading}
                  helpText={'This is an average across your products.'}
                />
              </Grid>
              <Grid
                md={6}
                xs={12}
                item
                className={classes.rightSideTileWrapper}
              >
                <AnalyticsTile
                  title="Benchmark Lead Time"
                  value={leadTimeBenchmark}
                  unit={unit}
                  isLoading={isLoading}
                  helpText={`This is the average across all ${providerName} sellers.`}
                />
              </Grid>
            </Grid>
            <Grid item className={classes.graphGridMargin}>
              <CostOfGoodsChart />
            </Grid>
          </Grid>
          <Grid
            item
            style={{ width: '100%', height: 500 }}
            className={classes.graphGridMargin}
          >
            <GoodsSoldGraph />
          </Grid>
        </Grid>
      </Card>
    </Grid>
  );
}
